<template>
  <div>
    <h2 class="text-center">선불 크레딧 관리</h2>
    <p class="text-center text-muted">
      선불(prepaid) 계정 목록입니다. 기관명을 클릭하면 잔액·지급·회수·소진순서·원장을 관리합니다.
      API 계정은 기관(institution)별로 크레딧이 분리됩니다.
    </p>

    <!-- 검색 + 새로고침 -->
    <div class="row justify-content-md-center" style="padding-bottom: 10px">
      <div class="col-md-5">
        <input
          v-model.trim="search"
          class="form-control form-control-sm"
          placeholder="사용자 ID 또는 기관명 검색"
        />
      </div>
      <div class="col-md-auto">
        <button type="button" class="btn btn-secondary btn-sm" :disabled="accountsLoading" @click="loadAccounts">
          <i class="fa fa-refresh"></i> 새로고침
        </button>
      </div>
    </div>

    <div v-if="accountsLoading" class="text-center text-muted py-3">
      <i class="fa fa-spinner fa-spin"></i> 불러오는 중...
    </div>

    <!-- 전체 선불 계정 표 -->
    <div v-else class="table-responsive-wrapper">
      <table class="table table-hover table-sm">
        <thead>
          <tr>
            <th>사용자 ID</th>
            <th>기관명</th>
            <th>유형</th>
            <th class="text-right">총 잔여 크레딧</th>
            <th class="text-center">관리</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredAccounts.length === 0">
            <td colspan="5" class="text-center text-muted">선불 계정이 없습니다.</td>
          </tr>
          <tr v-for="acct in filteredAccounts" :key="acct.username" style="cursor: pointer;" @click="openAccount(acct)">
            <td><strong>{{ acct.username }}</strong></td>
            <td>{{ acct.name || '-' }}</td>
            <td>
              <span class="badge" :class="acct.user_type === 'web' ? 'badge-secondary' : 'badge-info'">
                {{ acct.user_type === 'web' ? 'Web' : 'API' }}
              </span>
            </td>
            <td class="text-right"><span class="badge badge-primary">{{ acct.available }}</span> credit</td>
            <td class="text-center">
              <button class="btn btn-outline-primary btn-sm" @click.stop="openAccount(acct)">관리</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 계정 관리 모달 -->
    <b-modal v-model="modalVisible" size="xl" hide-footer :title="modalTitle">
      <div v-if="selected">
        <!-- 기관 선택 -->
        <div class="mb-3">
          <label class="small text-muted d-block mb-1">기관 선택 (API 계정은 기관별로 분리됩니다)</label>
          <div class="d-flex flex-wrap align-items-center">
            <button
              type="button"
              class="btn btn-sm mr-1 mb-1"
              :class="selectedInstitution === null ? 'btn-primary' : 'btn-outline-primary'"
              @click="selectWallet(null)"
            >디폴트 계정 ({{ accountWalletAvailable }})</button>
            <button
              v-for="w in institutionWallets"
              :key="w.institution"
              type="button"
              class="btn btn-sm mr-1 mb-1"
              :class="selectedInstitution === w.institution ? 'btn-primary' : 'btn-outline-primary'"
              @click="selectWallet(w.institution)"
            >{{ w.institution }} ({{ w.balance.available }})</button>
            <span v-if="selected.user_type !== 'web'" class="d-inline-flex align-items-center mb-1">
              <input
                v-model.trim="newInstitution"
                class="form-control form-control-sm d-inline-block"
                style="width: 180px;"
                placeholder="새 기관명 추가"
                @keyup.enter="addInstitution"
              />
              <button type="button" class="btn btn-outline-secondary btn-sm ml-1" @click="addInstitution">+ 기관</button>
            </span>
          </div>
          <small class="text-muted">현재 대상: <strong>{{ selectedInstitution || '디폴트 계정' }}</strong></small>
        </div>

        <div v-if="detailLoading" class="text-center text-muted py-3">
          <i class="fa fa-spinner fa-spin"></i> 불러오는 중...
        </div>

        <div v-else>
          <!-- 잔액 -->
          <b-card no-body class="mb-3">
            <b-card-body>
              <div class="row text-center">
                <div class="col"><div class="text-muted small">사용 가능</div><div class="h4 text-success">{{ balance.available }}</div></div>
                <div class="col"><div class="text-muted small">구매분</div><div class="h4">{{ balance.purchased }}</div></div>
                <div class="col"><div class="text-muted small">보너스</div><div class="h4">{{ balance.bonus }}</div></div>
                <div class="col"><div class="text-muted small">진행 중 분석(held)</div><div class="h4 text-warning">{{ balance.held }}</div></div>
              </div>
            </b-card-body>
          </b-card>

          <div class="row" v-if="canManageSelectedWallet">
            <!-- 지급 -->
            <div class="col-md-6">
              <b-card class="mb-3" header="크레딧 지급">
                <div class="form-group">
                  <label class="small mb-1">구매 크레딧</label>
                  <input v-model.number="grantForm.purchase_amount" type="number" min="0" class="form-control form-control-sm" />
                </div>
                <div class="form-group">
                  <label class="small mb-1">보너스 크레딧</label>
                  <input v-model.number="grantForm.bonus_amount" type="number" min="0" class="form-control form-control-sm" />
                </div>
                <div class="form-group">
                  <label class="small mb-1">보너스 유효기간(개월, 선택 — 기본 6개월)</label>
                  <input v-model.number="grantForm.expiry_months" type="number" min="1" class="form-control form-control-sm" placeholder="6" />
                </div>
                <div class="form-group">
                  <label class="small mb-1">사유 (필수)</label>
                  <input v-model.trim="grantForm.reason" class="form-control form-control-sm" placeholder="예: 2026-06 충전 입금 확인" />
                </div>
                <button type="button" class="btn btn-success btn-sm" :disabled="!canGrant || submitting" @click="grant">
                  <i class="fa fa-plus"></i> 지급
                </button>
              </b-card>
            </div>
            <!-- 회수 -->
            <div class="col-md-6">
              <b-card class="mb-3" header="미사용 크레딧 회수">
                <div class="form-group">
                  <label class="small mb-1">Lot ID (소진 순서 표에서 선택)</label>
                  <input v-model.trim="revokeForm.lot_id" class="form-control form-control-sm" placeholder="lot_id" />
                </div>
                <div class="form-group">
                  <label class="small mb-1">수량 (미입력 시 잔여 전체)</label>
                  <input v-model.number="revokeForm.amount" type="number" min="1" class="form-control form-control-sm" placeholder="전체" />
                </div>
                <div class="form-group">
                  <label class="small mb-1">사유 (필수)</label>
                  <input v-model.trim="revokeForm.reason" class="form-control form-control-sm" placeholder="예: 환불/정정" />
                </div>
                <button type="button" class="btn btn-danger btn-sm" :disabled="!canRevoke || submitting" @click="revoke">
                  <i class="fa fa-minus"></i> 회수
                </button>
              </b-card>
            </div>
          </div>
          <div v-else class="alert alert-info py-2">
            <i class="fa fa-info-circle"></i>
            이 API 계정의 <strong>디폴트 계정</strong>에는 크레딧을 지급하지 않습니다(기관에 유통하는 계정). 위에서 <strong>기관을 선택하거나 추가</strong>한 뒤 해당 기관에 지급하세요.
          </div>

          <!-- 소진 순서 + 보너스 만료 편집 -->
          <b-card no-body class="mb-3" header="소진 순서 (FIFO: 배치 우선, 배치 내 구매→보너스)">
            <div class="table-responsive-wrapper">
              <table class="table table-hover table-sm mb-0">
                <thead>
                  <tr>
                    <th>순서</th><th>유형</th><th>Lot ID</th><th>잔여</th><th>지급일</th><th>만료일</th><th class="text-center">동작</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="order.length === 0"><td colspan="7" class="text-center text-muted">소진 가능한 크레딧이 없습니다.</td></tr>
                  <tr v-for="(lot, idx) in order" :key="lot.lot_id">
                    <td>{{ idx + 1 }}</td>
                    <td><span v-if="lot.lot_type === 'purchase'" class="badge badge-primary">구매</span><span v-else class="badge badge-info">보너스</span></td>
                    <td><code>{{ shortId(lot.lot_id) }}</code></td>
                    <td>{{ lot.remaining }}</td>
                    <td>{{ formatDate(lot.granted_at) }}</td>
                    <td>
                      <span v-if="editingLotId !== lot.lot_id">{{ lot.expires_at ? formatDate(lot.expires_at) : '-' }}</span>
                      <input v-else v-model="editExpiryDate" type="date" class="form-control form-control-sm d-inline-block" style="width: 150px;" />
                    </td>
                    <td class="text-center">
                      <button class="btn btn-outline-danger btn-sm mr-1" @click="prefillRevoke(lot)" title="회수 대상 선택">
                        <i class="fa fa-undo"></i>
                      </button>
                      <template v-if="lot.lot_type === 'bonus'">
                        <button v-if="editingLotId !== lot.lot_id" class="btn btn-outline-secondary btn-sm" @click="startEditExpiry(lot)" title="만료일 편집">
                          <i class="fa fa-calendar"></i>
                        </button>
                        <span v-else>
                          <button class="btn btn-success btn-sm" :disabled="submitting" @click="saveExpiry(lot)"><i class="fa fa-check"></i></button>
                          <button class="btn btn-light btn-sm" @click="cancelEditExpiry"><i class="fa fa-times"></i></button>
                        </span>
                      </template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card>

          <!-- 원장 -->
          <b-card no-body class="mb-1" header="크레딧 원장 (감사 기록)">
            <div class="table-responsive-wrapper">
              <table class="table table-hover table-sm mb-0">
                <thead>
                  <tr><th>#</th><th>유형</th><th>수량</th><th>Lot</th><th>처리자</th><th>사유</th><th>시각</th></tr>
                </thead>
                <tbody>
                  <tr v-if="ledger.length === 0"><td colspan="7" class="text-center text-muted">원장 기록이 없습니다.</td></tr>
                  <tr v-for="entry in ledger" :key="entry.event_seq">
                    <td>{{ entry.event_seq }}</td>
                    <td>{{ entryTypeLabel(entry.entry_type) }}</td>
                    <td>{{ entry.amount }}</td>
                    <td><code v-if="entry.lot_id">{{ shortId(entry.lot_id) }}</code><span v-else>-</span></td>
                    <td>{{ entry.actor || '-' }}</td>
                    <td>{{ entry.reason || '-' }}</td>
                    <td>{{ formatDateTime(entry.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Admin from "@/models/admin";

const ENTRY_TYPE_LABELS = {
  grant_purchase: "구매 지급", grant_bonus: "보너스 지급", revoke_unused: "회수",
  correction: "정정", reserve: "예약", confirm: "차감", uncharged: "미차감(잔액 없음)", release: "예약 해제", expire: "만료"
};

function emptyBalance() {
  return { available: 0, purchased: 0, bonus: 0, held: 0, expiring_bonus: [], version: 0 };
}


export default {
  name: "PrepaidManagement",
  data() {
    return {
      accounts: [],
      accountsLoading: false,
      search: "",
      modalVisible: false,
      selected: null,
      wallets: [],
      selectedInstitution: null,
      newInstitution: "",
      detailLoading: false,
      submitting: false,
      balance: emptyBalance(),
      order: [],
      ledger: [],
      grantForm: { purchase_amount: 0, bonus_amount: 0, expiry_months: null, reason: "", idempotency_key: null, client_ts: null },
      revokeForm: { lot_id: "", amount: null, reason: "" },
      editingLotId: null,
      editExpiryDate: ""
    };
  },
  computed: {
    filteredAccounts() {
      if (!this.search) return this.accounts;
      const q = this.search.toLowerCase();
      return this.accounts.filter(a =>
        (a.username && a.username.toLowerCase().indexOf(q) !== -1) ||
        (a.name && a.name.toLowerCase().indexOf(q) !== -1)
      );
    },
    modalTitle() {
      if (!this.selected) return "";
      return "선불 크레딧 — " + this.selected.username + (this.selected.name ? " (" + this.selected.name + ")" : "");
    },
    institutionWallets() {
      return this.wallets.filter(w => w.scope === "institution" && w.institution);
    },
    accountWalletAvailable() {
      const acc = this.wallets.find(w => w.scope === "account");
      return acc ? acc.balance.available : 0;
    },
    canGrant() {
      const p = Number(this.grantForm.purchase_amount) || 0;
      const b = Number(this.grantForm.bonus_amount) || 0;
      return (p > 0 || b > 0) && !!this.grantForm.reason;
    },
    canRevoke() {
      return !!this.revokeForm.lot_id && !!this.revokeForm.reason;
    },
    canManageSelectedWallet() {
      // API/both prepaid accounts distribute credits to institutions; their default
      // account is not granted to. Web prepaid uses the default account as its wallet.
      if (this.selected && this.selected.user_type !== 'web' && this.selectedInstitution === null) return false;
      return true;
    }
  },
  mounted() {
    this.loadAccounts();
  },
  methods: {
    shortId(id) {
      if (!id) return "";
      const s = String(id);
      return s.length > 10 ? s.slice(0, 8) + "…" : s;
    },
    formatDate(iso) { return iso ? String(iso).split("T")[0] : "-"; },
    formatDateTime(iso) { return iso ? String(iso).replace("T", " ").split(".")[0] : "-"; },
    entryTypeLabel(t) { return ENTRY_TYPE_LABELS[t] || t; },
    errMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }
      return fallback;
    },
    loadAccounts() {
      this.accountsLoading = true;
      Admin.listPrepaidAccounts()
        .then(res => { this.accounts = res.data.accounts || []; })
        .catch(error => { this.$toastr.error(this.errMessage(error, "선불 계정 목록 조회 실패")); })
        .finally(() => { this.accountsLoading = false; });
    },
    openAccount(acct) {
      this.selected = acct;
      this.selectedInstitution = null;
      this.newInstitution = "";
      this.resetForms();
      this.modalVisible = true;
      this.loadWallets();
    },
    loadWallets() {
      if (!this.selected) return;
      this.detailLoading = true;
      Admin.getPrepaidWallets(this.selected.username)
        .then(res => {
          this.wallets = res.data.wallets || [];
          this.loadDetail();
        })
        .catch(error => {
          this.$toastr.error(this.errMessage(error, "지갑 목록 조회 실패"));
          this.detailLoading = false;
        });
    },
    selectWallet(institution) {
      this.selectedInstitution = institution;
      this.resetForms();
      this.loadDetail();
    },
    addInstitution() {
      const name = (this.newInstitution || "").trim();
      if (!name) return;
      const existing = this.wallets.find(w => w.scope === "institution" && w.institution === name);
      if (existing) {
        this.newInstitution = "";
        this.selectWallet(name);
        return;
      }
      // Persist an (empty) institution wallet so it stays listable — reads alone never create.
      this.submitting = true;
      Admin.createPrepaidWallet(this.selected.username, name)
        .then(() => {
          this.$toastr.success("기관 '" + name + "' 추가됨");
          this.newInstitution = "";
          return Admin.getPrepaidWallets(this.selected.username).then(res => {
            this.wallets = res.data.wallets || [];
            this.selectWallet(name);
          });
        })
        .catch(error => { this.$toastr.error(this.errMessage(error, "기관 추가 실패")); })
        .finally(() => { this.submitting = false; });
    },
    loadDetail() {
      if (!this.selected) return;
      this.detailLoading = true;
      const username = this.selected.username;
      const inst = this.selectedInstitution || undefined;
      Promise.all([
        Admin.getPrepaidBalance(username, inst),
        Admin.getPrepaidPreview(username, inst),
        Admin.getPrepaidLedger(username, inst)
      ])
        .then(([b, p, l]) => {
          this.balance = (b.data && b.data.balance) || emptyBalance();
          this.order = (p.data && p.data.order) || [];
          this.ledger = (l.data && l.data.entries) || [];
        })
        .catch(error => { this.$toastr.error(this.errMessage(error, "지갑 조회 실패")); })
        .finally(() => { this.detailLoading = false; });
    },
    resetForms() {
      this.grantForm = { purchase_amount: 0, bonus_amount: 0, expiry_months: null, reason: "", idempotency_key: null, client_ts: null };
      this.revokeForm = { lot_id: "", amount: null, reason: "" };
      this.editingLotId = null;
      this.editExpiryDate = "";
    },
    grant() {
      if (!this.canGrant) return;
      this.submitting = true;
      // grant 멱등키: 같은 지급의 재시도(응답 유실 등)는 같은 키로 dedupe 되어 이중충전을 막는다.
      // 성공했을 때만 키를 비워(아래 .then) 다음 지급이 새 키를 받도록 한다.
      if (!this.grantForm.idempotency_key) {
        this.grantForm.idempotency_key =
          (window.crypto && window.crypto.randomUUID)
            ? window.crypto.randomUUID()
            : (Date.now() + "-" + Math.random().toString(36).slice(2));
      }
      const payload = {
        purchase_amount: Number(this.grantForm.purchase_amount) || 0,
        bonus_amount: Number(this.grantForm.bonus_amount) || 0,
        reason: this.grantForm.reason,
        institution: this.selectedInstitution || undefined,
        idempotency_key: this.grantForm.idempotency_key
      };
      const months = Number(this.grantForm.expiry_months);
      if (months > 0) payload.expiry_months = months;
      Admin.grantPrepaid(this.selected.username, payload)
        .then(() => {
          this.$toastr.success("크레딧이 지급되었습니다.");
          this.grantForm = { purchase_amount: 0, bonus_amount: 0, expiry_months: null, reason: "", idempotency_key: null, client_ts: null };
          this.afterMutation();
        })
        .catch(error => { this.$toastr.error(this.errMessage(error, "지급 실패")); })
        .finally(() => { this.submitting = false; });
    },
    revoke() {
      if (!this.canRevoke) return;
      this.submitting = true;
      const payload = {
        lot_id: this.revokeForm.lot_id,
        reason: this.revokeForm.reason,
        institution: this.selectedInstitution || undefined
      };
      const amount = Number(this.revokeForm.amount);
      if (amount > 0) payload.amount = amount;
      Admin.revokePrepaid(this.selected.username, payload)
        .then(res => {
          if (res.data && res.data.status === "revoked") {
            this.$toastr.success("미사용 크레딧 " + res.data.amount + "개를 회수했습니다.");
          } else {
            this.$toastr.warning("회수 결과: " + (res.data && res.data.status));
          }
          this.revokeForm = { lot_id: "", amount: null, reason: "" };
          this.afterMutation();
        })
        .catch(error => { this.$toastr.error(this.errMessage(error, "회수 실패")); })
        .finally(() => { this.submitting = false; });
    },
    prefillRevoke(lot) {
      this.revokeForm.lot_id = lot.lot_id;
      this.revokeForm.amount = null;
    },
    startEditExpiry(lot) {
      this.editingLotId = lot.lot_id;
      this.editExpiryDate = lot.expires_at ? String(lot.expires_at).split("T")[0] : "";
    },
    cancelEditExpiry() {
      this.editingLotId = null;
      this.editExpiryDate = "";
    },
    saveExpiry(lot) {
      if (!this.editExpiryDate) { this.$toastr.error("만료일을 입력하세요."); return; }
      this.submitting = true;
      Admin.updatePrepaidLotExpiry(this.selected.username, lot.lot_id, {
        expires_at: this.editExpiryDate,
        reason: "operator expiry edit",
        institution: this.selectedInstitution || undefined
      })
        .then(() => {
          this.$toastr.success("보너스 만료일을 변경했습니다.");
          this.cancelEditExpiry();
          this.afterMutation();
        })
        .catch(error => { this.$toastr.error(this.errMessage(error, "만료일 변경 실패")); })
        .finally(() => { this.submitting = false; });
    },
    afterMutation() {
      // Refresh wallet list (new institution wallet may now exist) + current detail + account totals.
      Admin.getPrepaidWallets(this.selected.username)
        .then(res => { this.wallets = res.data.wallets || []; })
        .catch(() => {});
      this.loadDetail();
      this.loadAccounts();
    }
  }
};
</script>
